import { css, useTheme } from '@emotion/react'
import { useTranslation } from 'next-i18next'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button, Tab, TabPanel } from '../base-ui'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useRouter } from 'next/router'
import countries from './countries.json'

export default function LoginBox({ style }) {
    const router = useRouter()
    const { t } = useTranslation()
    const schemaSignUp = yup.object().shape({
        first_name: yup.string().required(t('enter your first name')),
        last_name: yup.string().required(t('enter your last name')),
        email: yup
            .string()
            .email(t('email address is invalid'))
            .required(t('enter your email address')),
        mobile: yup.string().required(t('enter your mobile number')),
        password: yup.string().required(t('enter a password')),
        password_repeat: yup
            .string()
            .test(
                'passwords-match',
                t('password and password repeat are not the same'),
                function (value) {
                    return this.parent.password === value
                }
            )
            .required(t('repeat your password here'))
    })
    const schemaLogin = yup.object().shape({
        email: yup
            .string()
            .email(t('email address is invalid'))
            .required(t('enter your email address')),
        password: yup.string().required(t('enter your password'))
    })
    const theme = useTheme()
    const formStyle = css`
        height: 100%;
        .form-body {
            display: grid;
            align-content: center;
            height: 100%;
            grid-template-columns: repeat(1, minmax(0, 1fr));
            gap: 5px;
            ${'' /* padding: 1rem; */}
        }
        .form-control {
            font-size: ${theme.fontSize.sm};
            width: 100%;
            label {
                display: block;
                margin-bottom: 0.5rem;
            }
            input,
            select {
                display: block;
                width: 100%;
                border-radius: ${theme.borderRadius.default};
                border: 1px solid ${theme.colors.lightGray};
                padding: 0.375rem 0.75rem;
                line-height: 1.5;
                background-clip: padding-box;
                &:focus {
                    color: ${theme.colors.secGray};
                    background-color: ${theme.colors.white};
                    border-color: ${theme.colors.secondaryLight};
                    outline: 0;
                    box-shadow: 0 0 0 0.2rem rgb(149 163 255 / 25%);
                }
            }
            select {
                background-position: ${['fa', 'ar'].indexOf(router.locale) !==
                    -1
                        ? 'left'
                        : 'right'}
                    0.5rem center;
            }
        }
        ${style}
    `
    const tabStyle = css`
        ${'' /* height: 100%; */}
        padding: 0.125rem;
        .button-group {
            height: 2.5rem;
            width: 100%;
            button {
                flex: 1 50%;
                box-shadow: none;
            }
        }
    `

    const {
        register: regL,
        handleSubmit: handleLogin,
        formState: { errors: errorsl }
    } = useForm({
        resolver: yupResolver(schemaLogin)
    })
    const {
        register: regR,
        handleSubmit: handleRegister,
        reset,
        formState: { errors: errorsr }
    } = useForm({
        resolver: yupResolver(schemaSignUp)
    })
    const [errorMsgLogin, setErrorMsgLogin] = useState('')
    const [errorMsgRegister, setErrorMsgRegister] = useState('')
    const login = async (data) => {
        if (errorMsgLogin) setErrorMsgLogin('')

        try {
            const response = await fetch('/api/auth/signin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    username: data.email,
                    password: data.password
                })
            })
            let responseObject = await response.json()
            if (response.status === 200) {
                if (
                    responseObject.returnUrl &&
                    responseObject.returnUrl[0] === '/'
                )
                    router.push(responseObject.returnUrl)
            } else {
                setErrorMsgLogin(responseObject.message)
            }
        } catch (error) {
            console.error('An unexpected error happened occurred:', error)
            setErrorMsgLogin(error.message)
        }
    }
    const register = async (data) => {
        if (errorMsgRegister) setErrorMsgRegister('')

        try {
            const response = await fetch('/api/auth/register', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            })
            let responseObject = await response.json()
            if (response.status === 200) {
                router.push('/auth/activate?receiver=' + data.email, null, {
                    locale: router.locale
                })
            } else if (response.status === 400) {
                if (responseObject.newCaptchaImage) {
                    reset({ ...data, captcha: '' })
                    // setCaptchaImage(responseObject.newCaptchaImage)
                }
                setErrorMsgRegister(t(responseObject.message))
            }
        } catch (error) {
            console.error('An unexpected error happened occurred:', error)
            setErrorMsgRegister(error.message)
        }
    }

    return (
        <Tab
            tabs={[{ label: t('registration') }, { label: t('login') }]}
            justify="center"
            css={tabStyle}
        >
            <TabPanel key={0} index={0}>
                <form onSubmit={handleRegister(register)} css={formStyle}>
                    <div className="form-body px-2 xl:px-10 justify-items-center">
                        <div className="flex flex-wrap md:flex-nowrap gap-2 w-full">
                            <div className="form-control mt-2 basis-full">
                                <label>{t('first name')}</label>
                                <input {...regR('first_name')} type="text" />
                                {errorsr?.first_name?.message}
                            </div>
                            <div className="form-control mt-2 basis-full">
                                <label>{t('last name')}</label>
                                <input {...regR('last_name')} type="text" />
                                {errorsr?.last_name?.message}
                            </div>
                        </div>

                        <div className="flex flex-wrap md:flex-nowrap gap-2 w-full">
                            <div className="form-control mt-2 basis-full">
                                <label>{t('mobile')}</label>
                                <input {...regR('mobile')} type="tel" />
                                {errorsr?.mobile?.message}
                            </div>
                            <div className="form-control mt-2 basis-full">
                                <label>{t('email')}</label>
                                <input {...regR('email')} type="email" />
                                {errorsr?.email?.message}
                            </div>
                        </div>

                        <div className="flex flex-wrap md:flex-nowrap gap-2 w-full">
                            <div className="form-control mt-2 basis-full">
                                <label>{t('password')}</label>
                                <input
                                    {...regR('password')}
                                    type="password"
                                    autoComplete="off"
                                />
                                {errorsr?.password?.message}
                            </div>
                            <div className="form-control mt-2 basis-full">
                                <label>{t('repeat password')}</label>
                                <input
                                    {...regR('password_repeat')}
                                    type="password"
                                    autoComplete="off"
                                />
                                {errorsr?.password_repeat?.message}
                            </div>
                        </div>
                        <div className="flex flex-wrap md:flex-nowrap gap-2 w-full">
                            <div className="form-control mt-2 basis-full">
                                <label>{t('country')}</label>
                                <select {...regR('country')}>
                                    {countries.map((c) => (
                                        <option key={c.name} value={c.name}>
                                            {c.name}
                                        </option>
                                    ))}
                                </select>
                                {errorsr?.country?.message}
                            </div>
                        </div>
                        <span className="text-red-500" style={{ height: 24 }}>
                            {t(errorMsgRegister)}
                        </span>
                        <Button
                            type="submit"
                            backgroundColor="secondary"
                            color="lighterGray"
                            className="mt-5 w-40"
                        >
                            {t('register')}
                        </Button>
                    </div>
                </form>
            </TabPanel>
            <TabPanel key={1} index={1}>
                <form onSubmit={handleLogin(login)} css={formStyle}>
                    <div className="form-body px-10 justify-items-center">
                        <div className="form-control mt-2">
                            <label>{t('email')}</label>
                            <input {...regL('email')} type="email" />
                            {errorsl?.email?.message}
                        </div>
                        <div className="form-control mt-2">
                            <label>{t('password')}</label>
                            <input {...regL('password')} type="password" />
                            {errorsl?.password?.message}
                        </div>
                        <span className="text-red-500" style={{ height: 24 }}>
                            {t(errorMsgLogin)}
                        </span>
                        <Button
                            type="submit"
                            backgroundColor="secondary"
                            color="lighterGray"
                            className="mt-5 w-40"
                        >
                            {t('login')}
                        </Button>
                    </div>
                </form>
            </TabPanel>
        </Tab>
    )
}
